import { useQuery } from '@tanstack/react-query'
import { pfetch } from '@/lib/phishtrainer/play-api'
import { playKeys } from '@/lib/phishtrainer/query-keys'
import type { UserGamificationSummary, Round } from '@/lib/phishtrainer/types'

// ============ Types ============

interface SummaryEnvelope {
  data?: UserGamificationSummary | null
}
interface HistoryEnvelope {
  data?: Round[] | null
}

// ============ Query Hooks ============

/**
 * The learner's gamification summary — points, tier, streak, weekly stats,
 * pillar slam, quests.
 *
 * Throws on a missing body rather than returning a zeroed summary: the /play
 * home renders an explicit error card for this, and a synthesised empty summary
 * would show a real-looking "0 points / Bronze" board instead of admitting the
 * fetch failed.
 */
export function usePlayDashboard(userId: string | undefined) {
  return useQuery({
    queryKey: playKeys.dashboard(),
    queryFn: async (): Promise<UserGamificationSummary> => {
      const res = await pfetch(`/api/phishtrainer/users/${userId}/gamification`)
      const json: SummaryEnvelope = await res.json()
      if (!json?.data) throw new Error('Could not load your progress')
      return json.data
    },
    enabled: !!userId,
  })
}

/** Recent rounds for the history card. */
export function usePlayRoundHistory(userId: string | undefined, limit = 10) {
  return useQuery({
    queryKey: playKeys.rounds.history({ limit }),
    queryFn: async (): Promise<HistoryEnvelope> => {
      const res = await pfetch(`/api/phishtrainer/users/${userId}/history?limit=${limit}`)
      if (!res.ok) throw new Error('Could not load your round history')
      return res.json()
    },
    // Normalize to an array here so the page never needs `?? []` (banned in
    // app/** by apps/web/CLAUDE.md).
    select: (res: HistoryEnvelope) => res.data ?? [],
    enabled: !!userId,
  })
}
